import { ChangeDetectorRef, Component, OnInit } from "@angular/core";
import { FormBuilder, FormControl, FormGroup, Validators } from "@angular/forms";
import { Router } from "@angular/router";
import nationsWithCodes from "app/auth/models/nations-with-codes";
import { IContentHeader } from "app/layout/components/content-header/content-header.component";
import { UtilsService } from "../../../../../auth/service/utils.service";
import { AuthenticationService } from "../../../../../auth/service";
import { ListService } from "../../../../../auth/service/list.service";
import { ICustomerForm, ICustomerType } from "../../../customer.interface";
import { CustomerService } from "../../../customer.service";
import fieldsInvoice from "../../../form-fields.json";
import { CitiesService } from "../../../../../auth/service/cities.service";

@Component({
  selector: "app-customers-add-form",
  templateUrl: "./customers-add-form.component.html",
  styleUrls: ["../edit/customers-edit-form.component.scss"],
})
export class CustomersAddFormComponent implements OnInit {
  public formGroup: FormGroup;
  public contentHeader: IContentHeader = null;
  public logoBase64: string = "";
  public fileName: string = null;
  private showErrors: boolean = false;
  public nationsWithCodes: { name: string; code: string }[] = nationsWithCodes;
  public active_table: string = "customer";
  public subjectTypeList: ICustomerType[] = [];
  public fieldsInvoice: any = fieldsInvoice.invoice;
  public fieldsPersonaFisica: any = fieldsInvoice.person;
  private subjectTypePerson: ICustomerType | undefined;

  constructor(
    public _utilService: UtilsService,
    public _formBuilder: FormBuilder,
    public _customerService: CustomerService,
    public router: Router,
    public _authenticationService: AuthenticationService,
    private _listService: ListService,
    public _changeDetectorRef: ChangeDetectorRef,
    public _citiesService: CitiesService,
  ) {
    this.contentHeader = {
      headerTitle: "TITLES.ADD_CUSTOMER",
      actionButton: false,
      breadcrumb: {
        type: "",
        links: [
          {
            name: "Home",
            isLink: true,
            link: "/",
          },
          {
            name: "CUSTOMERS",
            isLink: false,
          },
          {
            name: "LIST",
            isLink: true,
            link: "/dashboard/customers",
          },
          {
            name: "ADD",
            isLink: false,
          },
        ],
      },
    };

    this.formGroup = this._formBuilder.group({
      name: new FormControl("", [Validators.required, Validators.minLength(2), Validators.maxLength(150)]),
      pec: new FormControl("", [Validators.email, Validators.pattern("^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$")]),
      email: new FormControl("", [Validators.email, Validators.pattern("^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$")]),
      country: new FormControl(""),
      subject_type: new FormControl(null, [Validators.required]),
    });

    const fields = this.fieldsInvoice.concat(this.fieldsPersonaFisica);

    for (const field of fields) {
      let validators = [];
      if (field.validators?.required) {
        validators.push(Validators.required);
      }
      if (field.validators?.minlength) {
        validators.push(Validators.minLength(field.validators.minlength));
      }
      if (field.validators?.maxLength) {
        validators.push(Validators.maxLength(field.validators.maxLength));
      }
      if (field.validators?.pattern) {
        validators.push(Validators.pattern(field.validators.pattern));
      }

      if (field.type === "email") {
        validators.push(Validators.email);
      }

      this.formGroup.addControl(field.key, new FormControl(null, validators));
    }
  }

  async ngOnInit() {
    if (!this._authenticationService.hasCapability("create_customer")) {
      this._utilService.hideLoading();
      await this.router.navigateByUrl("/dashboard/main");
      return false;
    }

    this.subjectTypeList = await this._listService.getList("customer_type");
    this.subjectTypePerson = this.subjectTypeList.find((item: ICustomerType) => item.name.toLowerCase().includes("persona fisica"));

    this.fieldsInvoice = await this._customerService.fillDynamicFields(this.fieldsInvoice);
  }

  changeTab(tab: string) {
    this.active_table = this.active_table === tab ? "" : tab;
    this._changeDetectorRef.detectChanges();
  }

  fieldHasErrors(field_name: string): boolean {
    if (this.showErrors) {
      return this.formGroup.controls[field_name]?.status === "INVALID";
    } else {
      return this.formGroup.controls[field_name]?.status === "INVALID" && this.formGroup.controls[field_name].touched;
    }
  }

  async changeSubjectType() {
    if (this.formGroup.controls.subject_type.value === this.subjectTypePerson.id) {
      this.fieldsInvoice = [...this.fieldsInvoice, ...this.fieldsPersonaFisica];
    } else {
      this.fieldsInvoice = fieldsInvoice.invoice;
    }
    this.fieldsInvoice = await this._customerService.fillDynamicFields(this.fieldsInvoice);
  }

  async add() {
    if (!this._authenticationService.hasCapability("create_customer")) {
      this._utilService.hideLoading();
      await this.router.navigateByUrl("/dashboard/main");
      return false;
    }
    this.showErrors = true;
    // controllo valori
    if (!this.formGroup.value.fiscalcode && !this.formGroup.value.vat_number) {
      await this._utilService.showSWALError({ text: "INSERT_FISCALCODE_OR_VAT_NUMBER" });
      return false;
    }
    if (this.formGroup.errors !== null) {
      return false;
    }

    this._utilService.showLoading();

    let data: ICustomerForm = this.formGroup.value;
    data.name = String(data.name).trim();
    data.business_name = String(data.business_name).trim();

    if (data.subject_type !== this.subjectTypePerson.id) {
      for (const key of fieldsInvoice.person.map((field: any) => field.key)) {
        data[key] = null;
      }
    }

    await this._customerService
      .addCustomer(data)
      .then(async (response) => {
        if (this.logoBase64 && this.logoBase64.length > 0) {
          const fileExtension: string = this.logoBase64.substring("data:image/".length, this.logoBase64.indexOf(";base64"));
          const fileName: string = `logo-${this._utilService.slugify_me(data.name)}.${fileExtension}`;
          await this._utilService.upload_media("public", fileName, this.logoBase64).then(async (uploadFile: any) => {
            if (uploadFile) {
              await this._customerService.editCustomer(response.id, { logo: "public/" + fileName }).catch(async () => {
                this._utilService.hideLoading();
                await this._utilService.showSWALError({ text: "ERROR_ADD_CUSTOMER" });
              });
            }
          });
        }
        this._utilService.hideLoading();
        await this._utilService.showSWALSuccess({ text: "SUCCESS_ADD_CUSTOMER" });
        await this.router.navigateByUrl("/dashboard/customer/edit/" + response.id);
      })
      .catch(async () => {
        this._utilService.hideLoading();
        await this._utilService.showSWALError({ text: "ERROR_ADD_CUSTOMER" });
      });
  }
}
